import React from 'react';
import { styled } from 'styletron-react';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { useS3Image } from '../../hooks/useS3Image';
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';

const Page = styled('div', {
  minHeight: '100vh',
  backgroundColor: '#fff',
  fontFamily:
    "'Red Hat Text', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  color: '#111827',
});

const Content = styled('div', {
  maxWidth: '760px',
  margin: '0 auto',
  padding: '24px 20px 80px',
});

const Header = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  marginBottom: '24px',
});

const HeaderLeft = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const Logo = styled('img', {
  height: '40px',
  objectFit: 'contain',
});

const TenantName = styled('div', {
  fontSize: '16px',
  fontWeight: '600',
});

const Title = styled('h1', {
  fontSize: '32px',
  fontWeight: '700',
  margin: '8px 0 4px',
  lineHeight: '1.2',
});

const Subtitle = styled('p', {
  color: '#6B7280',
  fontSize: '14px',
  margin: '0 0 28px',
});

const SectionTitle = styled('h2', {
  fontSize: '20px',
  fontWeight: '700',
  margin: '28px 0 12px',
});

const Paragraph = styled('p', {
  fontSize: '15px',
  lineHeight: '1.6',
  margin: '0 0 12px',
});

const List = styled('ul', {
  paddingLeft: '20px',
  margin: '0 0 16px',
});

const OrderedList = styled('ol', {
  paddingLeft: '20px',
  margin: '0 0 16px',
});

const Item = styled('li', {
  fontSize: '15px',
  lineHeight: '1.7',
  marginBottom: '6px',
});

const ContactBlock = styled('div', {
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  padding: '16px 20px',
  marginTop: '24px',
});

const EFFECTIVE_DATE = '2026-08-21';

/**
 * Public account-deletion request page.
 *
 * Reachable at /requestdelete (e.g. https://www.capcana.app/requestdelete).
 * This URL is declared in the Google Play Data Safety form as the account
 * deletion link, so it must stay: active, publicly accessible worldwide,
 * and not require sign-in. Keep it English-only for now; a Spanish variant
 * will follow.
 */
export const RequestDeleteAccount: React.FC = () => {
  usePublicPageScroll();

  const { tenant } = useTenantBranding();
  const tenantName = tenant?.name || 'Cap Cana';
  const { imageUrl: logoUrl } = useS3Image(tenant?.settings?.logo);
  const supportEmail =
    (tenant?.settings as any)?.supportEmail || 'info@xhinola.io';
  const subject = encodeURIComponent(`Account deletion request — ${tenantName}`);

  return (
    <Page>
      <Content>
        <Header>
          <HeaderLeft>
            {logoUrl ? <Logo src={logoUrl} alt={tenantName} /> : null}
            <TenantName>{tenantName}</TenantName>
          </HeaderLeft>
        </Header>

        <Title>Delete your account</Title>
        <Subtitle>Last updated: {EFFECTIVE_DATE}</Subtitle>

        <Paragraph>
          This page explains how to request deletion of your {tenantName}{' '}
          account and the personal data associated with it. Deleting your
          account is permanent and cannot be undone.
        </Paragraph>

        <SectionTitle>Delete your account in the app</SectionTitle>
        <Paragraph>
          The fastest way to delete your account is directly from the mobile
          app:
        </Paragraph>
        <OrderedList>
          <Item>Open the {tenantName} app and sign in.</Item>
          <Item>Go to your Profile.</Item>
          <Item>Tap “Delete User”.</Item>
          <Item>Confirm the deletion when prompted.</Item>
        </OrderedList>
        <Paragraph>
          Once confirmed, your account is scheduled for deletion and you are
          signed out.
        </Paragraph>

        <SectionTitle>Request deletion by email</SectionTitle>
        <Paragraph>
          If you can no longer access the app, you can ask us to delete your
          account. Send an email to{' '}
          <a href={`mailto:${supportEmail}?subject=${subject}`}>
            {supportEmail}
          </a>{' '}
          from the email address linked to your account, and include:
        </Paragraph>
        <List>
          <Item>The full name on your account.</Item>
          <Item>The email address and/or phone number used to register.</Item>
          <Item>The subject line: “Account deletion request”.</Item>
        </List>
        <Paragraph>
          We may need to verify your identity before processing the request to
          protect your account from unauthorized deletion. We will confirm by
          email once your account has been deleted.
        </Paragraph>

        <SectionTitle>What data is deleted</SectionTitle>
        <Paragraph>
          When your deletion request is processed, we permanently remove the
          personal data tied to your account, including:
        </Paragraph>
        <List>
          <Item>Your profile information (name, email, phone, profile photo).</Item>
          <Item>Identity documents uploaded for access verification.</Item>
          <Item>Access codes, invitations, and visit history you created.</Item>
          <Item>App preferences and device/notification tokens.</Item>
        </List>

        <SectionTitle>What data may be retained</SectionTitle>
        <Paragraph>
          Some records are kept after account deletion only where we are legally
          required to, or where they are needed to comply with financial,
          security, or dispute-resolution obligations:
        </Paragraph>
        <List>
          <Item>
            Transaction and booking records required for accounting, tax, and
            fraud-prevention purposes. We do not store full payment card
            numbers.
          </Item>
          <Item>
            Security and access logs required by the property administration for
            safety and legal compliance.
          </Item>
        </List>
        <Paragraph>
          Retained records are kept only for as long as the applicable law
          requires, and are then deleted. Where possible, they are anonymized so
          they can no longer be linked back to you.
        </Paragraph>

        <SectionTitle>How long deletion takes</SectionTitle>
        <Paragraph>
          Account deletion requests are typically processed within 30 days.
          Backups containing your data are purged on our regular backup rotation
          cycle.
        </Paragraph>

        <SectionTitle>Contact</SectionTitle>
        <Paragraph>
          If you have any questions about deleting your account or your data,
          contact us at:
        </Paragraph>
        <ContactBlock>
          <div style={{ fontWeight: 600 }}>{tenantName}</div>
          <div>
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
          </div>
        </ContactBlock>
      </Content>
    </Page>
  );
};
